import { useQuery } from 'react-query';
import { archivosFetch } from '../../utils/fetch';
import { UserFilters, UserRow, UsersPage } from './types';

// Users listing for XD screen 8.
//
// `GET /api/users` only filters by `searchText` (first name, last name, email) and
// paginates on the server with `page` and `limit`. The XD "Services" and
// "Rôle" dropdowns have no parameter in the API.
//
// Applying them on the client over a server page would mislead: with "25
// lignes par page" three rows and a "1 - 25 sur 8" footer would show, and the
// service's users on other pages would not appear. What the API does
// offer is the full list when `page` and `limit` are not sent, and the users
// list of an organisation is short. So:
//
//   - without service nor role → server-side pagination, like the Home.
//   - with service and/or role → full list, filter and pagination here.
//                               The footer shows the real filtered total.
//
// Noted API gap: in the unpaginated response the backend ignores
// `searchText` (the `where` clause is only applied in the paginated branch) and does not
// sort; the search box is applied here too and sorted by `createdAt`
// descending, just as the API does in the paginated branch. The backend's
// search is case-sensitive (`Op.like`); this one is not.
//
// `GET /api/users` answers 404 with the empty list (and 200 with `totalItems`
// when there are results). The 404 is read as "none", not as an error.

export const USERS_KEY = 'USERS_LIST';

type Params = {
  filters: UserFilters;
  page: number;
  pageSize: number;
};

const isClientSide = (filters: UserFilters): boolean =>
  Boolean(filters.service) || Boolean(filters.role);

const matchesSearch = (user: UserRow, search: string): boolean => {
  if (!search) return true;
  const needle = search.toLowerCase();
  return [user.firstname, user.lastname, user.email].some((value) =>
    (value ?? '').toLowerCase().includes(needle),
  );
};

const matchesFilters = (user: UserRow, filters: UserFilters): boolean =>
  matchesSearch(user, filters.search) &&
  (!filters.service || (user.services ?? []).includes(filters.service)) &&
  (!filters.role || user.role === filters.role);

const byNewest = (a: UserRow, b: UserRow): number =>
  (b.createdAt ?? '').localeCompare(a.createdAt ?? '');

const buildUrl = ({ filters, page, pageSize }: Params): string => {
  const params = new URLSearchParams();
  if (!isClientSide(filters)) {
    params.set('page', String(page + 1));
    params.set('limit', String(pageSize));
  }
  if (filters.search) params.set('searchText', filters.search);

  const query = params.toString();
  return `${process.env.REACT_APP_API_URL}/api/users${query ? `?${query}` : ''}`;
};

const fetchUsers = async (params: Params): Promise<UsersPage> => {
  const response = await archivosFetch(buildUrl(params));

  if (response.status === 404) {
    return { users: [], totalItems: 0 };
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const body = await response.json();
  const users: UserRow[] = Array.isArray(body?.users) ? body.users : [];

  if (!isClientSide(params.filters)) {
    return {
      users,
      totalItems:
        typeof body?.totalItems === 'number' ? body.totalItems : users.length,
    };
  }

  const filtered = users
    .filter((user) => matchesFilters(user, params.filters))
    .sort(byNewest);
  const start = params.page * params.pageSize;

  return {
    users: filtered.slice(start, start + params.pageSize),
    totalItems: filtered.length,
  };
};

export const useUsers = (params: Params) => {
  const { filters, page, pageSize } = params;

  return useQuery<UsersPage, Error>(
    [USERS_KEY, filters.search, filters.service, filters.role, page, pageSize],
    () => fetchUsers(params),
    {
      // When changing page or filter the table keeps the previous
      // rows until the new ones arrive, instead of emptying.
      keepPreviousData: true,
      retry: false,
    },
  );
};
